import { usePathname } from 'next/navigation';
import { useCallback, useMemo } from 'react';

import { MarketType } from '@features/explore/constants';
import { useActivePersonaMode } from 'hooks/useActivePersonaMode';
import { useOrgSlug } from 'hooks/useOrgSlug';

const getMarketPath = (market: MarketType) => {
  switch (market) {
    case 'prediction-markets':
      return '/prediction-markets';
    default:
      return '/explore';
  }
};

export const useHref = () => {
  const orgSlug = useOrgSlug();
  const personaMode = useActivePersonaMode();
  const pathname = usePathname();

  // App pages live under `/${orgSlug}/${mode}`; null when there is no org context.
  const orgBase = useMemo(
    () => (orgSlug == null ? null : `/${orgSlug}/${personaMode}`),
    [orgSlug, personaMode],
  );

  const currentMarket = useMemo((): MarketType => {
    if (pathname.includes('/prediction-markets')) {
      return 'prediction-markets';
    }
    return 'demand';
  }, [pathname]);

  const workflowsHref = useCallback(
    (params?: Record<string, string>) => {
      if (orgBase == null) {
        return '/not-found';
      }
      const base = `${orgBase}/spend/workflows`;
      if (params == null) {
        return base;
      }
      const query = new URLSearchParams(params).toString();
      return query !== '' ? `${base}?${query}` : base;
    },
    [orgBase],
  );

  const workflowHref = useCallback(
    (id: string) => {
      if (orgBase == null) {
        return '/not-found';
      }
      return `${orgBase}/spend/workflows/${id}`;
    },
    [orgBase],
  );

  const vendorHref = useCallback(
    (id: string) => {
      if (orgBase == null) {
        return '/not-found';
      }
      return `${orgBase}/spend/vendors/${id}`;
    },
    [orgBase],
  );

  const receiptHref = useCallback(
    (id: string) => {
      if (orgBase == null) {
        return '/not-found';
      }
      return `${orgBase}/spend/invoices-and-receipts/receipt/${id}`;
    },
    [orgBase],
  );

  const purchaseOrderHref = useCallback(
    (id: string) => {
      if (orgBase == null) {
        return '/not-found';
      }
      return `${orgBase}/spend/purchase-orders/${id}`;
    },
    [orgBase],
  );

  const contractHref = useCallback(
    (contractId: string) => {
      if (orgBase == null) {
        return `/explore/contract/${contractId}`;
      }
      return `${orgBase}/explore/contract/${contractId}`;
    },
    [orgBase],
  );

  const invoiceHref = useCallback(
    (id: string) => {
      if (orgBase == null) {
        return '/not-found';
      }
      return `${orgBase}/spend/invoices-and-receipts/invoice/${id}`;
    },
    [orgBase],
  );

  const createHref = useCallback(
    (commodityId?: string, type?: 'demand' | 'supply') => {
      const base = orgBase == null ? `/create` : `${orgBase}/create`;
      const params = new URLSearchParams();
      if (commodityId != null) {params.set('commodityId', commodityId);}
      if (type != null) {params.set('type', type);}
      const query = params.toString();
      return query !== '' ? `${base}?${query}` : base;
    },
    [orgBase],
  );

  const procureHref = useCallback(() => {
    if (orgBase == null) {
      return '/not-found';
    }
    return `${orgBase}/procure`;
  }, [orgBase]);

  const cashBalanceHref = useCallback(() => {
    if (orgBase == null) {
      return '/not-found';
    }
    return `${orgBase}/cash-balance`;
  }, [orgBase]);

  const paymentLinksHref = useCallback(() => {
    if (orgBase == null) {
      return '/not-found';
    }
    return `${orgBase}/payment-links`;
  }, [orgBase]);

  const paymentLinkHref = useCallback(
    (id: string) => {
      if (orgBase == null) {
        return '/not-found';
      }
      return `${orgBase}/payment-links/${id}`;
    },
    [orgBase],
  );

  const paymentLinkBuilderHref = useCallback(() => {
    if (orgBase == null) {
      return '/not-found';
    }
    return `${orgBase}/payment-links/new`;
  }, [orgBase]);

  const payoutsHref = useCallback(() => {
    if (orgBase == null) {
      return '/not-found';
    }
    return `${orgBase}/payouts`;
  }, [orgBase]);

  const payoutHref = useCallback(
    (id: string) => {
      if (orgBase == null) {
        return '/not-found';
      }
      return `${orgBase}/payouts/${id}`;
    },
    [orgBase],
  );

  const invoicesHref = useCallback(() => {
    if (orgBase == null) {
      return '/not-found';
    }
    return `${orgBase}/invoices`;
  }, [orgBase]);

  const salesInvoiceHref = useCallback(
    (id: string) => {
      if (orgBase == null) {
        return '/not-found';
      }
      return `${orgBase}/invoices/${id}`;
    },
    [orgBase],
  );

  const invoiceBuilderHref = useCallback(
    (customerId?: string) => {
      if (orgBase == null) {
        return '/not-found';
      }
      const base = `${orgBase}/invoices/new`;
      return customerId != null ? `${base}?customer=${customerId}` : base;
    },
    [orgBase],
  );

  const invoiceEditHref = useCallback(
    (id: string) => {
      if (orgBase == null) {
        return '/not-found';
      }
      return `${orgBase}/invoices/${id}/edit`;
    },
    [orgBase],
  );

  const invoiceSetupHref = useCallback(() => {
    if (orgBase == null) {
      return '/not-found';
    }
    return `${orgBase}/invoices/setup`;
  }, [orgBase]);

  const customersHref = useCallback(() => {
    if (orgBase == null) {
      return '/not-found';
    }
    return `${orgBase}/customers`;
  }, [orgBase]);

  const customerHref = useCallback(
    (id: string) => {
      if (orgBase == null) {
        return '/not-found';
      }
      return `${orgBase}/customers/${id}`;
    },
    [orgBase],
  );

  const suppliersHref = useCallback(() => {
    if (orgBase == null) {
      return '/not-found';
    }
    return `${orgBase}/suppliers`;
  }, [orgBase]);

  const supplierHref = useCallback(
    (id: string) => {
      if (orgBase == null) {
        return '/not-found';
      }
      return `${orgBase}/suppliers/${id}`;
    },
    [orgBase],
  );

  // Public checkout — outside the org/mode (auth) tree.
  const publicCheckoutHref = useCallback((slug: string) => `/pay/${slug}`, []);

  const exploreHref = useCallback(
    (market: MarketType, options?: { withoutOrgSlug?: boolean }) => {
      if (orgBase == null || options?.withoutOrgSlug) {
        return getMarketPath(market);
      }
      return `${orgBase}${getMarketPath(market)}`;
    },
    [orgBase],
  );

  const commodityHref = useCallback(
    (commodityId: string) => {
      if (orgBase == null) {
        return `/explore/commodity/${commodityId}`;
      }
      return `${orgBase}/explore/commodity/${commodityId}`;
    },
    [orgBase],
  );

  const onboardingHref = useCallback(() => {
    return `/onboarding`;
  }, []);

  const onboardingFullUrl = useCallback(() => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/onboarding`;
  }, []);

  const predictionMarketHref = useCallback(
    (marketId?: string) => {
      const path = marketId != null ? `/prediction-markets/${marketId}` : '/prediction-markets';
      if (orgBase == null) {
        return path;
      }
      return `${orgBase}${path}`;
    },
    [orgBase],
  );

  const makePositionsHref = useCallback(
    (segment?: string) => {
      const base = orgBase == null ? '/positions' : `${orgBase}/positions`;
      if (segment == null) {
        return base;
      }
      return `${base}/${segment}`;
    },
    [orgBase],
  );

  const positionsHref = useCallback(() => makePositionsHref(), [makePositionsHref]);
  const myHedgesHref = useCallback(() => makePositionsHref('hedges'), [makePositionsHref]);
  const myPredictionsHref = useCallback(() => makePositionsHref('predictions'), [makePositionsHref]);
  const marketBriefingHref = useCallback(() => makePositionsHref('briefing'), [makePositionsHref]);
  const myNeedListHref = useCallback(() => makePositionsHref('need-list'), [makePositionsHref]);
  const mySupplyCommitmentsHref = useCallback(() => makePositionsHref('supply-commitments'), [makePositionsHref]);

  const supplyHref = useCallback(() => {
    if (orgBase == null) {
      return '/supply';
    }
    return `${orgBase}/supply`;
  }, [orgBase]);

  const supplyPartnerHref = useCallback(
    (partnerId: string) => {
      if (orgBase == null) {
        return `/supply/${partnerId}`;
      }
      return `${orgBase}/supply/${partnerId}`;
    },
    [orgBase],
  );

  const supplySectorHref = useCallback(
    (sectorId: string) => {
      if (orgBase == null) {
        return `/supply/sector/${sectorId}`;
      }
      return `${orgBase}/supply/sector/${sectorId}`;
    },
    [orgBase],
  );

  return {
    workflowsHref,
    workflowHref,
    vendorHref,
    receiptHref,
    purchaseOrderHref,
    contractHref,
    invoiceHref,
    createHref,
    procureHref,
    cashBalanceHref,
    paymentLinksHref,
    paymentLinkHref,
    paymentLinkBuilderHref,
    payoutsHref,
    payoutHref,
    invoicesHref,
    salesInvoiceHref,
    invoiceBuilderHref,
    invoiceEditHref,
    invoiceSetupHref,
    customersHref,
    customerHref,
    suppliersHref,
    supplierHref,
    publicCheckoutHref,
    exploreHref,
    commodityHref,
    onboardingHref,
    onboardingFullUrl,
    predictionMarketHref,
    positionsHref,
    myHedgesHref,
    myPredictionsHref,
    marketBriefingHref,
    myNeedListHref,
    mySupplyCommitmentsHref,
    supplyHref,
    supplyPartnerHref,
    supplySectorHref,
    currentMarket,
  };
};
